<template>
  <div>
    <table class="asd-table">
      <TableHeading v-if="showHeading"
                    :leadingCols="leadingCols"
                    :trailingCols="[]"
                    :cols="taskPropertiesToShow"
                    :defaultSorter="defaultSorter"
                    :userSorter="userSorter"
                    :addButton="addButton"
                    :menuItems="[
                      {icon: 'fa-file-csv', label: 'Exportovat zobrazené (CSV)', action: exportTableData},
                      {icon: 'fa-file-download', label: 'Exportovat vše (CSV)', action: exportAllTasksCsv},
                      {icon: 'fa-filter', label: 'Rozšířený filtr', action: () => showAdvancedFilter = !showAdvancedFilter}
                    ]"
                    @sorter-changed="onSorterChanged"
      >
        <SavedFilters
          :userFilter="userFilter"
          savedFilterId="task-table"
          @filter-changed="onSavedFilterChanged"
        ></SavedFilters>
      </TableHeading>
      <AdvancedFilter
        v-if="showFilter"
        :show="showAdvancedFilter"
        :cols="taskPropertiesToShow"
        :value="userFilter.advanced"
        @input="onAdvancedFilterChanged"
      ></AdvancedFilter>
      <TableFilter v-if="showFilter"
                   :leadingCols="leadingCols"
                   :trailingCols="[]"
                   :cols="taskPropertiesToShow"
                   :userFilter="userFilter.inline || {}"
                   @filter-changed="onInlineFilterChanged"
      ></TableFilter>
      <tbody>
        <TasksRow v-for="task in pagedTasks"
                  :key="task.id"
                  :taskId="task.id"
                  :propsToShow="taskPropertiesToShow"
                  :showProjectColumn="showProjectColumn"
                  :backProjectId="backProjectId"
        ></TasksRow>
      </tbody>
    </table>
    <Paginator v-model="currentPage"
               :numPages="numPages"
               @input="onPageChanged"
    ></Paginator>
  </div>
</template>

<script>
import _ from 'lodash'

import TasksRow from 'CORE/components/task/row'
import TableFilter from 'CORE/components/table-view/filter'
import AdvancedFilter from 'CORE/components/table-view/advanced-filter'
import TableHeading from 'CORE/components/table-view/heading'
import SavedFilters from 'CORE/components/table-view/saved-filters'
import Paginator from 'CORE/components/table-view/paginator'
import labels from 'CORE/utils/labels'
import ExportTableMixin from 'CORE/mixins/export-table'
import FileSaver from 'file-saver'
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'CORE/utils/date'
import moment from 'moment'

import { czSorter } from 'CORE/utils/sorters'
import { normalizeFts } from 'CORE/components/data-type/comparators'

export default {
    name: 'TasksTable',
    components: {
        TasksRow,
        TableFilter,
        AdvancedFilter,
        TableHeading,
        Paginator,
        SavedFilters,
    },
    mixins: [
        ExportTableMixin,
    ],
    props: {
        rootFilter: {
            required: false,
            default: _ => {},
            type: Object,
        },
        showHeading: {
            type: Boolean,
            default: true,
        },
        showFilter: {
            type: Boolean,
            default: true,
        },
        fixedPagination: {
            type: Boolean,
            default: false,
        },
        showProjectColumn: {
            type: Boolean,
            default: true,
        },
        projectTasks: {
            type: Boolean,
            default: false,
        },
        backProjectId: {
            type: String,
            required: false,
            default: _ => null,
        },
        taskStatus: {
            type: String,
            required: false,
            default: _ => '$all',
        },
        queryFilter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        querySorter: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        queryPager: {
            type: Object,
            required: false,
            default: _ => {return {}},
        },
        addButton: {
            type: Object,
            required: false,
            default: _ => null,
        },
    },
    data() {
        return {
            userFilter: { inline: {}, advanced: { logicalOperator: 'AND', children: [] } },
            userSorter: {},
            currentPage: 0,
            showAdvancedFilter: false,
        }
    },
    computed: {
        defaultSorter() {
            return _.get(this.$store.getters.currentUser, 'company.settings.gui.sorter.tasks', { column: '_type', order: 'asc' })
        },
        leadingCols() {
            const cols = [null, { label: 'Type', key: '_type' }]
            if (this.showProjectColumn) cols.push({ label: 'Projekt', key: '_project' })
            return cols
        },
        filteredTasks() {
            const filter = _.merge(this.userFilter.inline || {}, this.rootFilter)
            const tasks = this.$store.getters.tasksBy({
                projectId: this.projectTasks ? this.$route.params.id : undefined,
                taskStatus: this.taskStatus,
            })
            if (_.size(filter) === 0 && (!this.userFilter.advanced || !this.userFilter.advanced.children || this.userFilter.advanced.children.length === 0)) {
                return tasks
            }
            return _.filter(tasks, p => {
                let matches = 0
                _.each(filter, (filterValue, column) => {
                    if (column === '_type') {
                        const taskType = this.$store.getters.taskTypeById(_.get(p, 'taskType.id'))
                        const normalizedFilterValue = normalizeFts(filterValue)
                        const normalizedProjectName = normalizeFts(_.get(taskType, 'name'))
                        if (normalizedProjectName.search(normalizedFilterValue) >= 0) {
                            matches++
                        }
                    } else if (column === '_project') {
                        const project = this.$store.getters.getProjectById(_.get(p, 'project.id'))
                        const normalizedFilterValue = normalizeFts(filterValue)
                        const normalizedProjectName = normalizeFts(labels.project(project))
                        if (normalizedProjectName.search(normalizedFilterValue) >= 0) {
                            matches++
                        }
                    } else {
                        const normalizedFtsValue = normalizeFts(_.get(p, `propertyValues.${column}.$fts`, ''))
                        if (normalizedFtsValue.search(normalizeFts(filterValue)) >= 0) {
                            matches++
                        }
                    }
                })
                const inlineMatches = matches === _.size(filter)
                if (!inlineMatches) return false

                return this.evaluateAdvancedFilter(p, this.userFilter.advanced)
            })
        },
        sortedTasks() {
            const defaultColumn = 'system-name'
            let tasks = this.filteredTasks
            tasks = _.sortWith(tasks, (taskA, taskB) => {
                let sortingValueA; let sortingValueB
                if (_.get(this, 'userSorter.column') === '_project') {
                    sortingValueA = _.get(taskA, `project.id`)
                    sortingValueB = _.get(taskB, `project.id`)
                } else if (_.get(this, 'userSorter.column') === '_type') {
                    sortingValueA = _.get(this.$store.getters.taskTypeById(_.get(taskA, `taskType.id`)), 'name')
                    sortingValueB = _.get(this.$store.getters.taskTypeById(_.get(taskB, `taskType.id`)), 'name')
                } else {
                    sortingValueA = _.get(taskA, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                    sortingValueB = _.get(taskB, `propertyValues.${_.get(this, 'userSorter.column', defaultColumn)}.$srt`)
                }
                return czSorter(_.get(this, 'userSorter.order', 'desc'))(sortingValueA, sortingValueB)
            })
            return tasks
        },
        pagedTasks() {
            const paging = _.get(this.$store.getters.settings, 'gui.tasksPaging', 20)
            const start = this.currentPage * paging
            const end = start + paging
            return _.slice(this.sortedTasks, start, end)
        },
        numPages() {
            return Math.ceil(_.size(this.filteredTasks) / _.get(this.$store.getters.settings, 'gui.taskPaging', 20))
        },
        taskPropertiesToShow() {
            let propertyTypes = []
            _.forEach(_.map(this.projectTasks
                ? this.$store.getters.tasksByProjectId(this.$route.params.id)
                : this.$store.getters.tasks, 'propertyValues'), obj => {
                propertyTypes = _.union(propertyTypes, _.keys(obj))
            })
            propertyTypes = _.filter(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                if (_.includes(_.get(pt, 'settings.visible', []), 'tasksTable')) {
                    return p
                }
            })
            propertyTypes = _.union(propertyTypes, _.filter(_.keys(this.userFilter.inline || {}), k => !_.startsWith(k, '_')))
            propertyTypes = _.sortBy(propertyTypes, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                return _.parseInt(_.get(pt, 'settings.order', 0))
            })
            return _.defaults(propertyTypes, [])
        },
        taskPropertiesObjectsToShow() {
            const tp = {}
            if (this.taskPropertiesToShow) {
                for (const p of this.taskPropertiesToShow) {
                    _.set(tp, p, this.$store.getters.propertyTypeByKey(p))
                }
                return tp
            }
            return {}
        },
        projectTaskTypes() {
            const types = {}
            _.forEach(this.$store.getters.taskTypesByProjectId(this.$route.params.id), type => {
                _.set(types, type, this.$store.getters.taskTypeById(type))
            })
            return types
        },
        exportEntities() {
            return this.pagedTasks
        },
        exportKeys() {
            return this.taskPropertiesToShow
        },
    },
    watch: {
        rootFilter(nw) { // TODO upravit spravny merge filtru aby se nemusel resetovat takhle blbe
            if (nw === null) {
                this.userFilter = { inline: {}, advanced: { logicalOperator: 'AND', children: [] } }
            }
        },
    },
    mounted() {
        this.onSavedFilterChanged(this.queryFilter)
        if (_.isEmpty(this.querySorter)) {
            this.userSorter = this.defaultSorter
        } else {
            this.userSorter = this.querySorter
        }
        this.currentPage = _.get(this.queryPager, 'page', 0)
    },
    methods: {
        onInlineFilterChanged(params) {
            this.currentPage = 0
            const newFilter = Object.assign({}, this.userFilter, { inline: _.pickBy(params, f => _.size(f) > 0) })
            this.$set(this, 'userFilter', newFilter)
            this.$emit('filter-changed', this.userFilter)
        },
        onAdvancedFilterChanged(params) {
            this.currentPage = 0
            const newFilter = Object.assign({}, this.userFilter, { advanced: params })
            this.$set(this, 'userFilter', newFilter)
            this.$emit('filter-changed', this.userFilter)
        },
        onSavedFilterChanged(params) {
            this.currentPage = 0
            let newFilter = params
            if (params && !params.inline && !params.advanced) {
                newFilter = { inline: _.pickBy(params, f => _.size(f) > 0), advanced: { logicalOperator: 'AND', children: [] } }
            } else if (!params) {
                newFilter = { inline: {}, advanced: { logicalOperator: 'AND', children: [] } }
            }
            if (newFilter.advanced && newFilter.advanced.children && newFilter.advanced.children.length > 0) {
                this.showAdvancedFilter = true
            }
            this.$set(this, 'userFilter', newFilter)
            this.$emit('filter-changed', this.userFilter)
        },
        evaluateAdvancedFilter(task, filterRules) {
            if (!filterRules || !filterRules.logicalOperator) return true
            if (!filterRules.children || filterRules.children.length === 0) return true

            const evaluateRule = rule => {
                if (rule.type === 'query-builder-group') {
                    return this.evaluateAdvancedFilter(task, rule.query)
                } else if (rule.type === 'query-builder-rule') {
                    const column = rule.query.rule
                    const operator = rule.query.operator
                    const value = rule.query.value

                    let propValue = ''
                    if (column === '_type') {
                        const taskType = this.$store.getters.taskTypeById(_.get(task, 'taskType.id'))
                        propValue = _.get(taskType, 'name', '')
                    } else if (column === '_project') {
                        const project = this.$store.getters.getProjectById(_.get(task, 'project.id'))
                        propValue = labels.project(project)
                    } else {
                        propValue = _.get(task, `propertyValues.${column}.$fts`, '')
                    }

                    const normalizedFtsValue = normalizeFts(propValue)
                    const normalizedFilterValue = normalizeFts(value)

                    if (operator === 'rovná se' || operator === 'equals' || operator === '=') {
                        return normalizedFtsValue === normalizedFilterValue
                    } else if (operator === 'nerovná se' || operator === 'does not equal' || operator === '!=') {
                        return normalizedFtsValue !== normalizedFilterValue
                    } else if (operator === 'obsahuje' || operator === 'contains') {
                        return normalizedFtsValue.indexOf(normalizedFilterValue) >= 0
                    } else if (operator === 'neobsahuje' || operator === 'does not contain') {
                        return normalizedFtsValue.indexOf(normalizedFilterValue) === -1
                    } else if (operator === 'začíná na' || operator === 'begins with') {
                        return normalizedFtsValue.startsWith(normalizedFilterValue)
                    } else if (operator === 'končí na' || operator === 'ends with') {
                        return normalizedFtsValue.endsWith(normalizedFilterValue)
                    } else if (operator === 'je prázdné' || operator === 'is empty') {
                        return !normalizedFtsValue
                    } else if (operator === 'není prázdné' || operator === 'is not empty') {
                        return !!normalizedFtsValue
                    } else {
                        return normalizedFtsValue.indexOf(normalizedFilterValue) >= 0
                    }
                }
                return true
            }

            if (filterRules.logicalOperator === 'AND') {
                return filterRules.children.every(evaluateRule)
            } else if (filterRules.logicalOperator === 'OR') {
                return filterRules.children.some(evaluateRule)
            }
            return true
        },
        onSorterChanged(params) {
            this.userSorter = params
            this.$emit('sorter-changed', this.userSorter)
        },
        onPageChanged(page) {
            this.$emit('pager-changed', { page, numPages: this.numPages })
        },
        exportAllTasksCsv() {
            const tasksToExport = this.sortedTasks

            const pvExportValue = (v, k) => {
                const pt = this.$store.getters.propertyTypeByKey(k)
                const ptType = _.get(pt, 'type')
                const value = _.get(v, 'value.value')
                switch (ptType) {
                  	case 'NUMERIC':
                    	return _.get(v, 'value.value.number', '')
                    case 'DATE_TIME':
                        return moment(value, 'X').format(DATE_TIME_FORMAT)
                    case 'DATE':
                        return moment(value, 'X').format(DATE_FORMAT)
                    case 'LONGTEXT':
                        if (_.isString(value)) {
                            return value.replace(/"/g, '""')
                        }
                        return ''
                    case 'LINK_PERSON_RESOURCE':
                    case 'LINK_THING_RESOURCE':
                    case 'LINK_PLACE_RESOURCE':
                        return _.map(value, rid => {
                            return labels.resource(this.$store.getters.resourceById(rid))
                        }).join(', ')
                    case 'LINK_USER':
                        return _.map(value, uid => {
                            return _.get(this.$store.getters.userById(uid), 'username')
                        }).join(', ')
                    case 'BOOLEAN':
                        return value ? 'Ano' : 'Ne'
                    case 'ENTITY_STATE':
                        const allStates = _.get(this.$store.getters.currentUser, 'company.settings.entity-states.states', {})
                        return _.get(_.find(allStates, { id: value }), 'label', '-- unknown state --')
                    case 'LINK':
                        const linkType = _.get(pt, 'settings.link.entity')
                        return _.map(value, id => {
                            if (linkType === 'TASK') {
                                return labels.task(this.$store.getters.taskById(id))
                            } else if (linkType === 'PROJECT') {
                                return labels.project(this.$store.getters.getProjectById(id))
                            } else {
                                return '-- unknown link type --'
                            }
                        }).join(', ')
                    case 'STATIC_STRING':
                    default:
                        if (_.isObject(value) && _.has(value, 'unit')) {
                            const num = _.get(value, 'number', _.get(value, 'value'))
                            if (num !== undefined) {
                                return `${num} ${value.unit}`
                            }
                        }
                        return value
                }
            }

            let allHeaderKeys = []
            tasksToExport.forEach(p => {
                if (p.propertyValues) {
                    allHeaderKeys = _.union(allHeaderKeys, Object.keys(p.propertyValues))
                }
            })
            allHeaderKeys = _.sortBy(allHeaderKeys, p => {
                const pt = this.$store.getters.propertyTypeByKey(p)
                return _.parseInt(_.get(pt, 'settings.order', 0))
            })

            const headerRow = allHeaderKeys.map(key => _.get(this.$store.getters.propertyTypeByKey(key), 'name', key))

            const dataRows = tasksToExport.map(p => {
                return allHeaderKeys.map(key => {
                    const prop = _.get(p, `propertyValues.${key}`)
                    return prop ? pvExportValue(prop, key) : ''
                })
            })

            const csvContent = [headerRow, ...dataRows]
                .map(row => row.map(cell => `"${(cell === null || cell === undefined) ? '' : String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n')

            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' })
            FileSaver.saveAs(blob, 'tasks_export.csv')
        },
    },
}
</script>
